import React, { useEffect, useState } from 'react';
import axios from '../axiosConfig';
import { Modal, Button, Form, Table, ButtonGroup, ToggleButton } from 'react-bootstrap';

// Elegir a quién va una petición de la Bandeja: un ROL (le llega a todos los
// usuarios con ese rol) o un USUARIO concreto.
//
// Usa los mismos endpoints que el buscador de "Enviar petición" de
// InscripcionCursos: GET /role-paginate (?name=) y GET /users-paginate
// (?username=), paginados de a 10.
//
// onConfirmar recibe { role_id, destinatario_id, etiqueta }: uno de los dos
// ids viene en null, igual que en el resto de las peticiones del sistema.

const EnviarPeticionModal = ({
  show,
  titulo = 'Enviar a…',
  descripcion,
  onCancelar,
  onConfirmar,
}) => {
  const [modo, setModo] = useState('rol');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [resultado, setResultado] = useState({ data: [], last_page: 1 });
  const [elegido, setElegido] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (!show) return;
    const cargar = async () => {
      setCargando(true);
      try {
        const token = localStorage.getItem('token');
        const esRol = modo === 'rol';
        const { data } = await axios.get(esRol ? '/role-paginate' : '/users-paginate', {
          headers: { Authorization: `Bearer ${token}` },
          params: { page: pagina, [esRol ? 'name' : 'username']: busqueda || undefined },
        });
        setResultado((esRol ? data.roles : data.users) || { data: [], last_page: 1 });
      } catch (error) {
        console.error('Error cargando destinatarios:', error);
        setResultado({ data: [], last_page: 1 });
      } finally {
        setCargando(false);
      }
    };
    // Pequeña espera para no pedir al servidor en cada tecla.
    const t = setTimeout(cargar, 250);
    return () => clearTimeout(t);
  }, [show, modo, busqueda, pagina]);

  // Al abrir de nuevo, empezar limpio.
  useEffect(() => {
    if (show) {
      setElegido(null);
      setBusqueda('');
      setPagina(1);
    }
  }, [show]);

  const cambiarModo = (nuevo) => {
    setModo(nuevo);
    setElegido(null);
    setBusqueda('');
    setPagina(1);
  };

  const confirmar = () => {
    if (!elegido) return;
    onConfirmar(
      modo === 'rol'
        ? { role_id: elegido.id, destinatario_id: null, etiqueta: `rol ${elegido.name}` }
        : { role_id: null, destinatario_id: elegido.id, etiqueta: `usuario ${elegido.username}` }
    );
  };

  const filas = resultado.data || [];

  return (
    <Modal show={show} onHide={onCancelar} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {descripcion && <p className="mb-3">{descripcion}</p>}

        <ButtonGroup className="mb-3">
          <ToggleButton id="destino-rol" type="radio" variant="outline-primary" name="destino"
            value="rol" checked={modo === 'rol'} onChange={() => cambiarModo('rol')}>
            A un rol
          </ToggleButton>
          <ToggleButton id="destino-usuario" type="radio" variant="outline-primary" name="destino"
            value="usuario" checked={modo === 'usuario'} onChange={() => cambiarModo('usuario')}>
            A un usuario
          </ToggleButton>
        </ButtonGroup>

        <Form.Control
          className="mb-3"
          placeholder={modo === 'rol' ? 'Buscar rol…' : 'Buscar usuario…'}
          value={busqueda}
          onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
        />

        <Table size="sm" hover bordered>
          <thead>
            <tr><th>{modo === 'rol' ? 'Rol' : 'Usuario'}</th><th style={{ width: '120px' }}></th></tr>
          </thead>
          <tbody>
            {cargando && <tr><td colSpan={2} className="text-muted">Cargando…</td></tr>}
            {!cargando && filas.length === 0 && <tr><td colSpan={2} className="text-muted">Sin resultados</td></tr>}
            {!cargando && filas.map((f) => {
              const activo = elegido && elegido.id === f.id;
              return (
                <tr key={f.id} className={activo ? 'table-primary' : ''}>
                  <td>{modo === 'rol' ? f.name : f.username}</td>
                  <td className="text-center">
                    <Button size="sm" variant={activo ? 'primary' : 'outline-primary'} onClick={() => setElegido(f)}>
                      {activo ? 'Elegido' : 'Elegir'}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        <div className="d-flex justify-content-between align-items-center">
          <Button size="sm" variant="secondary" disabled={pagina <= 1} onClick={() => setPagina(pagina - 1)}>Anterior</Button>
          <span className="small text-muted">Página {pagina} de {resultado.last_page || 1}</span>
          <Button size="sm" variant="secondary" disabled={pagina >= (resultado.last_page || 1)} onClick={() => setPagina(pagina + 1)}>Siguiente</Button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onCancelar}>Cancelar</Button>
        <Button variant="success" disabled={!elegido} onClick={confirmar}>
          {elegido ? `Enviar al ${modo === 'rol' ? 'rol' : 'usuario'} ${modo === 'rol' ? elegido.name : elegido.username}` : 'Elija un destinatario'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EnviarPeticionModal;
